import { StyledRow } from "App/global-style";
import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getPayment,
  getPaymentRestriction,
  isAllowed,
} from "Store/akaAdvice/interfaces/getters";
import {
  AkaAdviceCheckboxes,
  AkaAdvicePriceFormValues,
} from "../../interfaces";
import { ContentWrapper } from "../../styled";

import CollapsibleContainer from "Components/CollapsibleContainer";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { DetailsCardWrapper } from "Components/InsuranceDetailsCard/styled";
import { VALUE_TYPE } from "Components/NumberValue";
import { DisabilityPensionInfo } from "Features/CheckPrice/Products/Advice/DisabilityPensionInfo";
import { AdvicePlotDisability } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/AdvicePlots/Disability/AdvicePlotDisability";
import { AlreadyInsured } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/AlreadyInsured";
import { ProductCoverages } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/ProductCoverages";
import { roundToNearestHundred } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/utils";
import { useAdviceDetailsFormValues } from "Features/CheckPrice/Products/Advice/ProductPrice/hooks/stateSelectors";
import { mapInsuranceNameToAdviceCoverageName } from "Features/CheckPrice/Products/Advice/ProductPrice/utils";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { useAkaAdviceNetIncomeSelector } from "Store/akaAdvice/akaAdviceSelectors";
import { akaAdvicePriceCalculationRequestStart } from "Store/akaAdvice/slice";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useIsAkademikerneBranding } from "Store/partners/partnerHooks";
import { TextLanguage } from "Store/texts/interfaces";
import { FormikProps } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { AdviceCardComment } from "../../AdviceCardComment";
import { AdviceCardHeader } from "../../AdviceCardHeader";
import { InsurancePrice } from "../../AdvicePrice/InsurancePrice";
import { AdvisorUwInfo } from "../../AdvisorUwInfo";
import { getMonthly } from "../../AkaAdviceCharts/AkaAdviceChartDisability/akaAdviceChartDisabilityMonthlyConfig";
import { CloseToMaxAgeWarning } from "../../CloseToMaxAgeWarning";
import { usePriceStatus } from "../../hooks/usePriceStatus";
import { getCoveragesSelectedInCard } from "../../utils";
import { DisabilityOneTimePaymentForm } from "../Forms/DisabilityOneTimePayment/DisabilityOneTimePaymentForm";
import { DisabilityPaymentAfter3Months } from "../Forms/DisabilityOneTimePayment/DisabilityPaymentAfter3Months";
import { IndividualDisabilityForm } from "../Forms/IndividualDisabilityForm";
import { DisabilityAdviceText } from "../Texts/DisabilityAdviceText";
import {
  DISABILITY_PENSION_END_AGE,
  DISABILITY_PENSION_MAX_AGE,
} from "../disabilityAdviceCardConstants";

export interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  adviceCard: any;
  lang: TextLanguage;
}

export const DisabilityAdviceCard = ({
  formProps,
  need,
  adviceCard,
  lang,
}: Props): React.ReactElement | null => {
  const isAkaBranding = useIsAkademikerneBranding();
  const dispatch = useDispatch();
  const adviceDetails = useAdviceDetailsFormValues();
  const isAdvisor = useIsAdvisorSelector();
  const netIncome = useAkaAdviceNetIncomeSelector();
  const selected = formProps.values.disabilityCheckbox;
  const currentSliderValue = formProps.values.disabilityInsuredSumSlider;
  const {
    priceIsChanging,
    setPriceIsChanging,
    priceIsLoading,
    setPriceIsLoading,
  } = usePriceStatus();
  const insuranceType = adviceCard.insuranceType[lang];

  const recalculatePrice = (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ): void => {
    setPriceIsChanging(false);
    setPriceIsLoading(true);
    dispatch(
      akaAdvicePriceCalculationRequestStart({
        isCollectiveAdvice: isAkaBranding,
        priceFormValues: formValues,
        specificProduct,
      }),
    );
  };

  if (
    !adviceDetails ||
    !isAllowed(need, ProductCoverageName.DISABILITY_FLEX_PENSION)
  ) {
    return null;
  }

  const paymentRestriction = getPaymentRestriction(
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  );

  const effectivePaymentRateDisabilityFlex = getPayment(
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  ).effectivePaymentRate;
  const netValue = getMonthly(
    (currentSliderValue ?? 0) * effectivePaymentRateDisabilityFlex,
  );
  const monthlyNetNavPayout = getMonthly(need.existingCoverages.NAV_PAYOUT);
  const monthlyNetEmployerPaidDisabilityPension = getMonthly(
    need.existingCoverages.EMPLOYER_PAYOUT,
  );

  return (
    <div className="AdviceCard_DISABILITY_FLEX">
      <DetailsCardWrapper
        key={insuranceType}
        adviceDetailsCard
        selected={selected}
      >
        <StyledRow>
          <AdviceCardHeader
            insuranceId={AkaAdviceCheckboxes.DISABILITY_CHECKBOX}
            insuranceType={insuranceType}
          />
          <InsurancePrice
            needName={NeedName.DISABILITY}
            productCoverages={getCoveragesSelectedInCard(
              need,
              formProps.values,
              isAdvisor,
            )}
            priceIsChanging={priceIsChanging}
            priceIsLoading={priceIsLoading}
          />
        </StyledRow>
        <ContentWrapper className="AdviceCardContent">
          <DisabilityAdviceText
            isAdvisor={isAdvisor}
            coverageNeed={need}
            adviceTexts={{
              ...adviceCard.adviceTexts,
              adviceUnavailableMessages: adviceCard.adviceUnavailableMessages,
            }}
            lang={lang}
          />
          <CollapsibleContainer isOpen={selected}>
            <AdvicePlotDisability
              monthlyIncome={roundToNearestHundred(getMonthly(netIncome))}
              monthlyNetNavPayout={roundToNearestHundred(monthlyNetNavPayout)}
              monthlyNetEmployerPaidDisabilityPension={roundToNearestHundred(
                monthlyNetEmployerPaidDisabilityPension,
              )}
              monthlyPaymentWithDisabilityInsurance={roundToNearestHundred(
                netValue +
                  monthlyNetNavPayout +
                  monthlyNetEmployerPaidDisabilityPension,
              )}
              valueType={VALUE_TYPE.NOK}
              advicePlot={adviceCard.advicePlot}
              lang={lang}
            />
            <IndividualDisabilityForm
              formProps={formProps}
              need={need}
              recalculatePrice={recalculatePrice}
              setPriceIsChanging={setPriceIsChanging}
              adviceCard={adviceCard}
              lang={lang}
            />

            {isAdvisor && (
              <AdvisorUwInfo
                insuranceName={INSURANCE_NAME_VALUE.DISABILITY_FLEX}
                sliderValue={currentSliderValue ?? 0}
                paymentRestriction={paymentRestriction}
                uwInfoTexts={adviceCard.advisorTexts.uwInfoTexts}
                lang={lang}
              />
            )}

            <DisabilityPensionInfo />

            <CloseToMaxAgeWarning
              need={need}
              coverageName={ProductCoverageName.DISABILITY_FLEX_PENSION}
              warningTexts={adviceCard.warnings}
              lang={lang}
              warningVariables={{
                maxAge: DISABILITY_PENSION_MAX_AGE,
                endAge: DISABILITY_PENSION_END_AGE,
              }}
            />

            {isAdvisor && (
              <DisabilityPaymentAfter3Months
                adviceDetails={adviceDetails}
                formProps={formProps}
                need={need}
                recalculatePrice={recalculatePrice}
                adviceCard={adviceCard}
                lang={lang}
              />
            )}

            <DisabilityOneTimePaymentForm
              adviceDetails={adviceDetails}
              formProps={formProps}
              need={need}
              recalculatePrice={recalculatePrice}
              displayPrice={false}
              adviceCard={adviceCard}
              lang={lang}
            />

            <AlreadyInsured insuranceType={insuranceType} />
            <ProductCoverages
              selected={selected}
              readableInsuranceType={insuranceType}
              backendInsuranceName={ProductBackendName.DISABILITY}
              coverageGroup={mapInsuranceNameToAdviceCoverageName(
                INSURANCE_NAME_VALUE.DISABILITY_FLEX,
              )}
              disabilityOneTimePayment={
                need.advice[INSURANCE_NAME_VALUE.DISABILITY_FLEX]?.payments[
                  ProductCoverageName.DISABILITY_FLEX_CAPITAL
                ]
              }
            />
          </CollapsibleContainer>
          <AdviceCardComment
            commentId="disabilityAdviceComment"
            formProps={formProps}
            label="Notater om anbefaling og kundens valg av uføreforsikring"
          />
        </ContentWrapper>
      </DetailsCardWrapper>
    </div>
  );
};
